import { useCallback, useState } from "react";
import { useTranslation } from "@coworker/locales";
import SelectMultipleItemSheet from "@coworker/apprestructured/src/task-management/components/SelectMultipleItemSheet/SelectMultipleItemSheet";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { formatLocationFilter } from "@coworker/apprestructured/src/task-management/util";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { VisibilityCondition } from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";

export type MultipleListItem = {
  value: string;
  label: string;
  visibility?: VisibilityCondition;
};

type SelectMultipleListItemProps = {
  title: string;
  selectedItems: MultipleListItem[];
  items: MultipleListItem[];
  onConfirmClick: (items: MultipleListItem[]) => void;
  maxItems?: number;
  maxItemsWarningKey?: string;
  search?: boolean;
  label?: string;
  showError?: boolean;
  errorMessage?: string;
};

function SelectMultipleListItem({
  title,
  selectedItems,
  items,
  onConfirmClick,
  maxItems,
  maxItemsWarningKey,
  search,
  label,
  showError,
  errorMessage,
}: SelectMultipleListItemProps) {
  const [draftSelectedValues, setDraftSelectedValues] = useState<string[]>([]);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  const getItemsByValues = useCallback(
    (values: string[]) => items.filter((item) => values.includes(item.value)),
    [items]
  );

  const handleOpenClick = useCallback(() => {
    setDraftSelectedValues(selectedItems.map((item) => item.value));
    setVisible(true);
  }, [selectedItems]);

  const handleConfirmClick = useCallback(() => {
    onConfirmClick(getItemsByValues(draftSelectedValues));
    setVisible(false);
  }, [draftSelectedValues, getItemsByValues, onConfirmClick]);

  const selectedDescription =
    selectedItems.length > 0
      ? selectedItems
          .map((item) => formatLocationFilter(item.value, t))
          .join(", ")
      : "";

  return (
    <>
      <FixaListViewItem
        title={t(title)}
        inset
        onClick={handleOpenClick}
        emphasised={selectedItems.length > 0}
        description={selectedDescription}
        control="navigational"
        controlIcon={null}
        addOn={
          showError && errorMessage ? (
            <ErrorAddOn>{errorMessage}</ErrorAddOn>
          ) : null
        }
      />
      <SelectMultipleItemSheet
        title={t(title)}
        selectedValues={draftSelectedValues}
        items={items.map((item) => ({
          value: item.value,
          label: formatLocationFilter(item.value, t),
        }))}
        onSelectedValuesChange={setDraftSelectedValues}
        onConfirmClick={handleConfirmClick}
        onClose={() => setVisible(false)}
        visible={visible}
        maxItems={maxItems}
        maxItemsWarningKey={maxItemsWarningKey}
        search={search}
        label={label}
      />
    </>
  );
}

export default SelectMultipleListItem;
